import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import { CRITERIA_TEMPLATES } from '@/features/criteria/criteria-templates';
import {
  fetchCriteria,
  fetchCriterionById,
  insertCriterion,
  updateCriterion,
  deleteCriterion,
} from '@/lib/queries/criteria';
import type {
  Criterion,
  CriterionType,
  CreateCriterionInput,
  UpdateCriterionInput,
} from '@/features/criteria/types';

const CRITERIA_KEY = 'criteria';
const CRITERION_KEY = 'criterion';
const TEMPLATES_KEY = 'criteria-templates';

// =========================================
// Queries
// =========================================

/** Fetch all criteria for the current user */
export function useCriteria(options?: { type?: CriterionType; category?: string }) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [CRITERIA_KEY, user?.id, options],
    queryFn: async (): Promise<Criterion[]> => {
      if (!user) return [];

      const { data, error } = await fetchCriteria(user.id, options);
      if (error) throw error;
      return (data ?? []) as Criterion[];
    },
    enabled: !!user,
  });
}

/** Fetch a single criterion by ID */
export function useCriterion(criterionId: string | undefined) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: [CRITERION_KEY, criterionId],
    queryFn: async (): Promise<Criterion | null> => {
      if (!criterionId || !user) return null;

      const { data, error } = await fetchCriterionById(criterionId);
      if (error) throw error;
      return data as Criterion;
    },
    enabled: !!criterionId && !!user,
  });
}

/** Return pre-defined templates (client-side constants, no DB call) */
export function useCriteriaTemplates(category?: string) {
  return useQuery({
    queryKey: [TEMPLATES_KEY, category],
    queryFn: (): Criterion[] => {
      if (category) {
        return CRITERIA_TEMPLATES.filter((t) => t.category === category);
      }
      return CRITERIA_TEMPLATES;
    },
    staleTime: Infinity,
  });
}

// =========================================
// Mutations
// =========================================

/** Create a new criterion */
export function useCreateCriterion() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (input: CreateCriterionInput): Promise<Criterion> => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await insertCriterion({
        user_id: user.id,
        name: input.name,
        description: input.description ?? null,
        type: input.type,
        config: input.config,
        weight: input.weight,
        category: input.category ?? null,
        is_template: false,
      });

      if (error) throw error;
      return data as Criterion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRITERIA_KEY] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create criterion');
    },
  });
}

/** Update an existing criterion */
export function useUpdateCriterion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateCriterionInput;
    }): Promise<Criterion> => {
      const { data, error } = await updateCriterion(id, updates);

      if (error) throw error;
      return data as Criterion;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [CRITERIA_KEY] });
      queryClient.invalidateQueries({ queryKey: [CRITERION_KEY, data.id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update criterion');
    },
  });
}

/** Delete a criterion */
export function useDeleteCriterion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (criterionId: string): Promise<void> => {
      const { error } = await deleteCriterion(criterionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRITERIA_KEY] });
      toast.success('Criterion deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete criterion');
    },
  });
}

/** Clone a template into user's own criteria */
export function useCloneTemplate() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async (templateId: string): Promise<Criterion> => {
      if (!user) throw new Error('Not authenticated');

      // Lookup from local templates
      const tpl = CRITERIA_TEMPLATES.find((t) => t.id === templateId);
      if (!tpl) throw new Error('Template not found');

      // Create a copy for the user
      const { data, error } = await insertCriterion({
        user_id: user.id,
        name: tpl.name,
        description: tpl.description,
        type: tpl.type,
        config: tpl.config,
        weight: tpl.weight,
        category: tpl.category,
        is_template: false,
      });

      if (error) throw error;
      return data as Criterion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRITERIA_KEY] });
      toast.success('Template cloned to your criteria');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to clone template');
    },
  });
}
